/**
 * Accounting Tags Zustand Store
 *
 * Global state management for accounting tags.
 * Provides tag list, loading state, and actions to fetch/manage tags.
 */

import { create } from 'zustand';
import type { Tag } from '@/types/accountingTags';
import { getTags } from '@/services/accountingTagsApi';

interface AccountingTagsState {
  /** All tags available in the system */
  tags: Tag[];
  /** Loading state for initial fetch */
  loading: boolean;
  /** Error message if fetch fails */
  error: string | null;
  /** Fetch all tags from API */
  fetchTags: () => Promise<void>;
  /** Clear error state */
  clearError: () => void;
}

/**
 * Zustand store for accounting tags
 *
 * @example
 * const { tags, loading, fetchTags } = useAccountingTagsStore();
 *
 * useEffect(() => {
 *   fetchTags();
 * }, []);
 */
export const useAccountingTagsStore = create<AccountingTagsState>((set) => ({
  tags: [],
  loading: false,
  error: null,

  fetchTags: async () => {
    set({ loading: true, error: null });
    try {
      const tags = await getTags();
      set({ tags, loading: false });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch tags';
      set({ error: errorMessage, loading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
